import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@mui/styles";
import { green } from "@mui/material/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	Divider,
	Typography,
} from "@mui/material";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		farewellMessage: "",
		isDefault: false,
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [waProfile, setWaProfile] = useState({ waName: "", waStatus: "" });
	const [profileLoading, setProfileLoading] = useState(false);
	const [profileSaving, setProfileSaving] = useState(false);
	const [picPreview, setPicPreview] = useState(null);
	const [picBase64, setPicBase64] = useState(null);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				setWhatsApp(data);

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	const handleLoadProfile = async () => {
		setProfileLoading(true);
		try {
			const { data } = await api.get(`/whatsapp/${whatsAppId}/profile`);
			setWaProfile({ waName: data.name || "", waStatus: data.status || "" });
		} catch (err) {
			toastError(err);
		} finally {
			setProfileLoading(false);
		}
	};

	const handlePicChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			const b64 = reader.result;
			setPicPreview(b64);
			setPicBase64(b64);
		};
		reader.readAsDataURL(file);
	};

	const handleSaveProfile = async () => {
		setProfileSaving(true);
		try {
			await api.put(`/whatsapp/${whatsAppId}/profile`, {
				name: waProfile.waName || undefined,
				status: waProfile.waStatus,
				picBase64: picBase64 || undefined,
			});
			toast.success("Perfil WhatsApp atualizado!");
			setPicBase64(null);
		} catch (err) {
			toastError(err);
		} finally {
			setProfileSaving(false);
		}
	};

	const handleSaveWhatsApp = async values => {
		const whatsappData = { ...values, queueIds: selectedQueueIds };

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: i18n.t("whatsappModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="isDefault"
												checked={values.isDefault}
											/>
										}
										label={i18n.t("whatsappModal.form.default")}
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.farewellMessage")}
										type="farewellMessage"
										multiline
										rows={5}
										fullWidth
										name="farewellMessage"
										error={
											touched.farewellMessage && Boolean(errors.farewellMessage)
										}
										helperText={
											touched.farewellMessage && errors.farewellMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={selectedIds => setSelectedQueueIds(selectedIds)}
								/>
								{whatsAppId && (
									<>
										<Divider style={{ marginTop: 16, marginBottom: 8 }} />
										<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
											<Typography variant="subtitle2" color="textSecondary">
												Perfil WhatsApp
											</Typography>
											<Button size="small" onClick={handleLoadProfile} disabled={profileLoading}>
												{profileLoading ? "Carregando..." : "Carregar perfil atual"}
											</Button>
										</div>
										<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
											{picPreview && (
												<img src={picPreview} alt="preview" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }} />
											)}
											<Button variant="outlined" size="small" component="label">
												Alterar foto
												<input type="file" accept="image/*" hidden onChange={handlePicChange} />
											</Button>
										</div>
										<TextField
											label="Nome do perfil WA"
											fullWidth
											variant="outlined"
											margin="dense"
											value={waProfile.waName}
											onChange={e => setWaProfile(p => ({ ...p, waName: e.target.value }))}
										/>
										<TextField
											label="Status/recado do WA"
											fullWidth
											variant="outlined"
											margin="dense"
											value={waProfile.waStatus}
											onChange={e => setWaProfile(p => ({ ...p, waStatus: e.target.value }))}
										/>
										<Button
											size="small"
											variant="outlined"
											color="primary"
											onClick={handleSaveProfile}
											disabled={profileSaving}
											style={{ marginTop: 8 }}
										>
											{profileSaving ? "Salvando..." : "Salvar perfil WA"}
										</Button>
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);
